import { useAuth } from "@/auth/auth";
import { useOrganization } from "@/client/backend/organization";
import { useUser } from "@/client/backend/user";
import { useOrganizationIOSSnapshot, type IOSSnapshot } from "@/client/backend/iosSnapshots";
import { useOrganizationTunnels } from "@/client/backend/tunnels";
import { getAssetsKey, useAssets } from "@/client/director/assets";
import { orgRequestOptions } from "@/client/director/org";
import { NewTunnelDialog } from "@/components/network/new-tunnel-dialog";
import { PlaygroundSnapshotPicker } from "@/components/PlaygroundSnapshotPicker";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { formatSize } from "@/lib/format";
import { IOS_MODEL_LABEL, IOS_MODEL_NAMES, type IOSModel } from "@/lib/ios-model";
import { IOS_VERSIONS, playgroundCommand, playgroundPreviewUrl, type PlaygroundConfiguration } from "@/lib/playground-configuration";
import { expandDeviceStateKey, parseLaunchEnvironment } from "@/lib/device-operations";
import { useDeviceStateOff } from "@/lib/device-state-preference";
import { useOrganizationScopes } from "@/lib/scopes";
import { formatErrorMessage } from "@/utils/format";
import type { Limrun } from "@limrun/api";
import { ChevronDown, Loader, Package, Search, Upload, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode, type UIEvent } from "react";
import SparkMD5 from "spark-md5";
import { mutate } from "swr";

const ASSET_LIST_LIMIT = 500;
const ASSET_PAGE_SIZE = 25;

type Asset = Limrun.AssetListResponse[number];

function isAppStoreAsset(asset: Asset): boolean {
  return asset.name.startsWith('appstore/');
}

function getAssetDisplayName(asset: Asset): string {
  return asset.displayName || (isAppStoreAsset(asset) ? asset.name.replace('appstore/', '') : asset.name);
}

function assetMatchesSearch(asset: Asset, search: string): boolean {
  if (!search) {
    return true;
  }
  return `${getAssetDisplayName(asset)} ${asset.name}`.toLowerCase().includes(search);
}

type IOSVersion = keyof typeof IOS_VERSIONS;
type PlaygroundOS = 'Android 14' | 'Android 15' | keyof typeof IOS_VERSIONS;

function isIOSVersion(os: PlaygroundOS): os is IOSVersion {
  return os in IOS_VERSIONS;
}

type AndroidModel = NonNullable<
  NonNullable<Limrun.AndroidInstanceCreateParams["spec"]>["model"]
>;

function ConfigurationOption({ id, title, children }: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="grid min-w-0 items-start gap-x-6 gap-y-2 @min-[32rem]/configuration:grid-cols-[11rem_minmax(0,1fr)]">
      <div id={id} className="text-sm font-medium leading-5 @min-[32rem]/configuration:py-2">{title}</div>
      <div className="min-w-0 space-y-2">
        {children}
      </div>
    </div>
  );
}

export function PlaygroundConfigurationForm({ initialConfiguration, defaultToSnapshotOS = false, onStart, isCreating = false }: {
  initialConfiguration: PlaygroundConfiguration;
  defaultToSnapshotOS?: boolean;
  onStart: (configuration: PlaygroundConfiguration, options?: { replaceSnapshot: boolean }) => void;
  isCreating?: boolean;
}) {
  const auth = useAuth();
  const [organization] = useOrganization();
  const requestOptions = orgRequestOptions(organization === "loading" ? undefined : organization.tid);
  const [selectedOS, setSelectedOS] = useState<PlaygroundOS>(
    `${initialConfiguration.platform === 'ios' ? 'iOS' : 'Android'} ${initialConfiguration.osVersion}` as PlaygroundOS
  );
  const snapshotOSDefaultPending = useRef(defaultToSnapshotOS);
  // The selected iOS version's clue and models; undefined while an Android version is selected.
  const selectedIOS = isIOSVersion(selectedOS) ? IOS_VERSIONS[selectedOS] : undefined;
  const [selectedAssets, setSelectedAssets] = useState<string[]>(initialConfiguration.assets);
  const [labelRows, setLabelRows] = useState(() => [...Object.entries(initialConfiguration.labels).map(([key, value], id) => ({ id, key, value })), { id: Object.keys(initialConfiguration.labels).length, key: "", value: "" }]);
  function updateLabel(id: number, field: "key" | "value", value: string) {
    setLabelRows(rows => {
      const updated = rows.map(row => row.id === id ? { ...row, [field]: value } : row);
      const last = updated[updated.length - 1];
      if (last.key || last.value) {
        updated.push({ id: last.id + 1, key: "", value: "" });
      } else {
        const previous = updated[updated.length - 2];
        // Keep focus in the cleared row instead of leaving two empty rows at the end.
        if (previous?.id === id && !previous.key && !previous.value) updated.pop();
      }
      return updated;
    });
  }
  const { labels, labelError } = useMemo(() => {
    try {
      const keys = new Set<string>();
      const entries = labelRows.filter(row => row.key.trim() || row.value).map(row => {
        const key = row.key.trim();
        if (!key) throw new Error("Enter a key for each label.");
        if (key.includes("=")) throw new Error("Label keys cannot contain =.");
        if (keys.has(key)) throw new Error(`The label key "${key}" is already used.`);
        if (key === IOS_MODEL_LABEL) throw new Error("The console's device model label is set automatically.");
        keys.add(key);
        return [key, row.value] as const;
      });
      return { labels: Object.fromEntries(entries), labelError: undefined };
    } catch (error) {
      return { labels: {}, labelError: (error as Error).message };
    }
  }, [labelRows]);
  const [showUploadDropzone, setShowUploadDropzone] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedSize, setUploadedSize] = useState<string>('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadAbortController, setUploadAbortController] = useState<(() => void) | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');
  const [visibleAssetCount, setVisibleAssetCount] = useState(ASSET_PAGE_SIZE);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedIOSModel, setSelectedIOSModel] = useState<IOSModel>(
    initialConfiguration.platform === 'ios' ? initialConfiguration.model : 'iphone',
  );
  const [selectedAndroidModel, setSelectedAndroidModel] = useState<AndroidModel>(initialConfiguration.platform === 'android' ? initialConfiguration.model : 'phone');
  const { can, denied } = useOrganizationScopes(organization);
  const canStartInstance = can(selectedIOS ? "ios" : "android", "all");
  const viewOnlyRole = denied(selectedIOS ? "ios" : "android", "all");
  const organizationId = organization === "loading" ? undefined : organization.tid;
  const canReadTunnels = can("tunnel", "read");
  const canManageTunnels = can("tunnel", "all");
  const [creatingTunnelFor, setCreatingTunnelFor] = useState<string>();
  const { data: tunnelData, error: tunnelError, mutate: refreshTunnels } = useOrganizationTunnels(
    canReadTunnels ? organizationId : undefined,
  );
  const [tunnelSelection, setTunnelSelection] = useState(() => ({ organizationId, name: initialConfiguration.tunnel }));
  useEffect(() => {
    setTunnelSelection(current => current.organizationId === organizationId ? current : {
      organizationId, name: current.organizationId === undefined ? current.name : undefined,
    });
  }, [organizationId]);
  const selectedTunnel = tunnelSelection.organizationId === organizationId && tunnelSelection.name
    ? { id: tunnelSelection.name, name: tunnelSelection.name } : undefined;
  const activeTunnels = (tunnelData?.data?.tunnels ?? []).filter(tunnel => tunnel.online);
  const activeTunnel = activeTunnels.find(tunnel => tunnel.name === selectedTunnel?.name);
  const tunnelsLoaded = tunnelData?.data !== undefined;
  const tunnelLoadError = tunnelError?.message ?? tunnelData?.error?.message;
  const tunnelUnavailable = !!selectedTunnel && (!canReadTunnels || !!tunnelLoadError || !activeTunnel);
  const [environmentRows, setEnvironmentRows] = useState(() => [
    ...Object.entries(initialConfiguration.env ?? {}).map(([key, value], id) => ({ id, key, value })),
    { id: Object.keys(initialConfiguration.env ?? {}).length, key: "", value: "" },
  ]);
  function updateEnvironment(id: number, field: "key" | "value", value: string) {
    setEnvironmentRows(rows => {
      const updated = rows.map(row => row.id === id ? { ...row, [field]: value } : row);
      const last = updated[updated.length - 1];
      if (last.key || last.value) updated.push({ id: last.id + 1, key: "", value: "" });
      return updated;
    });
  }
  const { user } = useUser();
  const [deviceStateOff] = useDeviceStateOff();
  // Honor the old browser opt-out as the default; an explicit selection controls this launch.
  const [snapshotSelection, setSnapshotSelection] = useState(() => ({
    organizationId, key: deviceStateOff ? undefined : initialConfiguration.deviceState, createNew: false,
  }));
  useEffect(() => {
    setSnapshotSelection(current => current.organizationId === organizationId ? current : {
      organizationId,
      key: current.organizationId === undefined ? current.key : undefined,
      createNew: false,
    });
  }, [organizationId]);
  const deviceState = selectedIOS && snapshotSelection.organizationId === organizationId ? snapshotSelection.key : undefined;
  const creatingSnapshot = !!selectedIOS && snapshotSelection.organizationId === organizationId && snapshotSelection.createNew;
  const canReadSnapshots = can("ios", "read");
  const snapshotKey = deviceState ? expandDeviceStateKey(deviceState, user === "loading" ? "" : user.emailAddress) : "";
  const snapshotKeyError = deviceState?.includes("{user}") && (user === "loading" || !user.emailAddress)
    ? "Your email is needed for a {user} snapshot key. Reload the page if it does not load."
    : snapshotKey && !/^[A-Za-z0-9][A-Za-z0-9._@:+-]{0,127}$/.test(snapshotKey)
      ? "Use up to 128 characters: start with a letter or digit, then letters, digits, or . _ - @ : +."
      : undefined;
  const { data: snapshot, error: snapshotLoadError, mutate: refreshSnapshot } = useOrganizationIOSSnapshot(
    deviceState && !snapshotKeyError && canReadSnapshots ? organizationId : undefined, snapshotKey,
  );
  useEffect(() => {
    if (!snapshotOSDefaultPending.current || !deviceState || !organizationId || !canReadSnapshots || snapshotKeyError || snapshotLoadError || snapshot === undefined) return;
    snapshotOSDefaultPending.current = false;
    const version = Object.entries(IOS_VERSIONS).find(([, version]) => version.osVersion === snapshot?.iosVersion);
    // Keep an explicitly selected device family and its apps if the snapshot's runtime cannot run them.
    if (snapshot?.saved && version && (version[1].models as readonly IOSModel[]).includes(selectedIOSModel)) {
      setSelectedOS(version[0] as IOSVersion);
    }
  }, [deviceState, organizationId, canReadSnapshots, snapshotKeyError, snapshotLoadError, snapshot, selectedIOSModel]);
  const snapshotValidationError = deviceState ? snapshotKeyError
    ?? (!canReadSnapshots ? "Snapshots require permission to read this organization's iOS devices." : undefined)
    ?? (snapshotLoadError ? "Could not load this snapshot. Retry or select None." : undefined)
    ?? (snapshotSelection.createNew && snapshot ? "This key already exists. Choose the existing snapshot or enter a different key." : undefined)
    : undefined;
  const snapshotIncompatible = !!deviceState && !!snapshot?.saved && !!snapshot.iosVersion && snapshot.iosVersion !== selectedIOS?.osVersion;
  const snapshotError = snapshotValidationError ?? (snapshotIncompatible
    ? `This snapshot requires iOS ${snapshot.iosVersion}. Select that version, choose another snapshot, or start fresh.` : undefined);
  const snapshotLoading = !!deviceState && !snapshotError && snapshot === undefined;
  const snapshotUnavailable = (creatingSnapshot && !deviceState) || !!snapshotError || snapshotLoading;

  function selectSnapshot(key?: string, saved?: IOSSnapshot) {
    snapshotOSDefaultPending.current = false;
    // Reselecting the displayed key must keep shared links scoped to each reviewer.
    const selectedKey = deviceState?.includes("{user}") && !snapshotKeyError && key === snapshotKey ? deviceState : key;
    setSnapshotSelection({ organizationId, key: selectedKey, createNew: false });
    const version = Object.entries(IOS_VERSIONS).find(([, version]) => version.osVersion === saved?.iosVersion);
    if (version) {
      setSelectedOS(version[0] as IOSVersion);
      if (!(version[1].models as readonly IOSModel[]).includes(selectedIOSModel)) {
        setSelectedIOSModel(version[1].models[0]);
        setSelectedAssets([]);
      }
    }
  }
  const [openUrl, setOpenUrl] = useState(initialConfiguration.openUrl ?? "");
  const [inactivityTimeout, setInactivityTimeout] = useState(initialConfiguration.inactivityTimeout ?? "");
  const { env, launchError } = useMemo(() => {
    try {
      const params = new URLSearchParams({ platform: selectedIOS ? "ios" : "android" });
      const keys = new Set<string>();
      for (const row of environmentRows.filter(row => row.key || row.value)) {
        if (!row.key || row.key.includes("=")) throw new Error("Enter an environment variable name without =.");
        if (keys.has(row.key)) throw new Error(`The environment variable "${row.key}" is already used.`);
        keys.add(row.key);
        params.append("env", `${row.key}=${row.value}`);
      }
      if (inactivityTimeout && !/^(?:[0-9]+(?:\.[0-9]+)?(?:h|m|s))+$/.test(inactivityTimeout)) {
        throw new Error("Enter an inactivity timeout such as 5m or 1h, or leave it empty for the organization default.");
      }
      return { env: parseLaunchEnvironment(params), launchError: undefined };
    } catch (error) {
      return { env: {}, launchError: (error as Error).message };
    }
  }, [environmentRows, selectedIOS, inactivityTimeout]);
  // Fetch user assets and App Store assets separately so App Store entries are not
  // squeezed out when the user's asset list hits the API limit.
  const organizationTid = organization !== "loading" ? organization.tid : undefined;
  const { data: userAssets } = useAssets({
    organizationTid,
    limit: ASSET_LIST_LIMIT,
  });
  const { data: appStoreAssets } = useAssets({
    organizationTid,
    includeAppStore: true,
    limit: ASSET_LIST_LIMIT,
    namePrefixFilter: 'appstore/',
  });
  const assets = useMemo<Limrun.AssetListResponse | "loading">(() => {
    if (userAssets === "loading" || appStoreAssets === "loading") {
      return "loading";
    }
    return [
      ...appStoreAssets.filter(isAppStoreAsset),
      ...userAssets.filter(asset => !isAppStoreAsset(asset)),
    ];
  }, [appStoreAssets, userAssets]);

  useEffect(() => {
    setVisibleAssetCount(ASSET_PAGE_SIZE);
  }, [assetSearch, selectedOS]);

  const handleAssetMenuScroll = (event: UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    if (target.scrollHeight - target.scrollTop - target.clientHeight < 32) {
      setVisibleAssetCount(count => count + ASSET_PAGE_SIZE);
    }
  };

  const userAssetsKey = getAssetsKey({
    organizationTid: organization !== "loading" ? organization.tid : undefined,
    limit: ASSET_LIST_LIMIT,
  });

  // Check if file is a valid archive (ZIP/APK or GZIP/tar.gz)
  const isValidArchive = async (file: File): Promise<boolean> => {
    const buffer = await file.slice(0, 4).arrayBuffer();
    const bytes = new Uint8Array(buffer);
    // ZIP magic bytes: PK\x03\x04 (also covers APK)
    const isZip = bytes[0] === 0x50 && bytes[1] === 0x4B && bytes[2] === 0x03 && bytes[3] === 0x04;
    // GZIP magic bytes: \x1f\x8b (covers tar.gz)
    const isGzip = bytes[0] === 0x1F && bytes[1] === 0x8B;
    return isZip || isGzip;
  };

  // Calculate MD5 hash of a file using spark-md5
  const calculateMD5 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const chunkSize = 2097152; // 2MB chunks for better performance
      const spark = new SparkMD5.ArrayBuffer();
      const fileReader = new FileReader();
      let currentChunk = 0;
      const chunks = Math.ceil(file.size / chunkSize);

      fileReader.onload = (e) => {
        spark.append(e.target?.result as ArrayBuffer);
        currentChunk++;

        if (currentChunk < chunks) {
          loadNext();
        } else {
          resolve(spark.end());
        }
      };

      fileReader.onerror = () => {
        reject(new Error('Failed to read file for MD5 calculation'));
      };

      const loadNext = () => {
        const start = currentChunk * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        fileReader.readAsArrayBuffer(file.slice(start, end));
      };

      loadNext();
    });
  };

  // Upload file with progress tracking using XMLHttpRequest
  const uploadWithProgress = (
    url: string,
    file: File,
    onProgress: (progress: number) => void,
    onSizeProgress: (sizeProgress: string) => void
  ): { promise: Promise<void>; abort: () => void } => {
    const xhr = new XMLHttpRequest();

    const promise = new Promise<void>((resolve, reject) => {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
          onSizeProgress(`${formatSize(event.loaded)} / ${formatSize(event.total)}`);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed: Network error'));
      });

      xhr.addEventListener('abort', () => {
        reject(new DOMException('Upload cancelled', 'AbortError'));
      });

      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      xhr.send(file);
    });

    return { promise, abort: () => xhr.abort() };
  };

  // Cancel upload
  const handleCancelUpload = () => {
    if (uploadAbortController) {
      uploadAbortController();
      setUploadAbortController(null);
    }
  };

  // Handle file upload
  const handleUploadApp = async () => {
    if (!selectedFile) return;

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadError(null);

      // Validate file is a valid archive
      const isValid = await isValidArchive(selectedFile);
      if (!isValid) {
        setUploadError("Invalid file format. Please upload an APK for Android and ZIP, or tar.gz for iOS.");
        setIsUploading(false);
        return;
      }

      // Calculate MD5 of the file
      const fileMD5 = await calculateMD5(selectedFile);

      // Get or create asset to get signed upload URL
      const assetResponse = await auth.regionClient.assets.getOrCreate({
        name: selectedFile.name,
      }, requestOptions);

      // Check if file already exists with same MD5
      if (assetResponse.md5 && assetResponse.md5 === fileMD5) {
        // File already exists, no need to upload
        toast({
          title: "Asset Already Exists",
          description: `${selectedFile.name} is already uploaded.`,
        });
      } else {
        // Upload file to signed URL with progress tracking
        const { promise, abort } = uploadWithProgress(
          assetResponse.signedUploadUrl,
          selectedFile,
          setUploadProgress,
          setUploadedSize
        );

        setUploadAbortController(() => abort);
        await promise;

        toast({
          title: "Upload Complete",
          description: `${selectedFile.name} has been uploaded successfully.`,
        });
      }

      if (userAssetsKey) {
        await mutate(userAssetsKey);
      }

      // Auto-select the uploaded asset
      if (!selectedAssets.includes(selectedFile.name)) {
        setSelectedAssets(prev => [...prev, selectedFile.name]);
      }

      // Hide dropzone and reset state
      setShowUploadDropzone(false);
      setSelectedFile(null);
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        toast({
          title: "Upload Cancelled",
          description: "The upload was cancelled.",
        });
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        setUploadError(formatErrorMessage(errorMessage));
        toast({
          title: "Upload Failed",
          description: formatErrorMessage(errorMessage),
          variant: "destructive",
        });
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadedSize('');
      setUploadAbortController(null);
    }
  };

  // Effect to start upload when file is selected
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally only trigger on selectedFile change
  useEffect(() => {
    if (selectedFile && !isUploading) {
      handleUploadApp();
    }
  }, [selectedFile]);

  function playgroundConfiguration(): PlaygroundConfiguration {
    const common = { assets: selectedAssets, labels, tunnel: selectedTunnel?.name, ...(Object.keys(env).length > 0 && { env }), ...(openUrl && { openUrl }), ...(inactivityTimeout && { inactivityTimeout }) };
    return selectedIOS
      ? { ...common, platform: "ios", osVersion: selectedIOS.osVersion, model: selectedIOSModel, ...(deviceState && { deviceState }) }
      : { ...common, platform: "android", osVersion: selectedOS === "Android 14" ? "14" : "15", model: selectedAndroidModel };
  }

  async function copyConfiguration(format: "command" | "url") {
    if (!organizationId || tunnelUnavailable || labelError || launchError || snapshotUnavailable) return;
    try {
      const configuration = playgroundConfiguration();
      const value = format === "command"
        ? playgroundCommand(configuration)
        : playgroundPreviewUrl(configuration, organizationId, window.location.origin);
      await navigator.clipboard.writeText(value);
      toast({
        title: format === "command" ? "Command copied" : "Preview URL copied",
        ...(format === "command" && organization !== "loading" && {
          description: `Run with the CLI signed in to ${organization.displayName}.`,
        }),
      });
    } catch {
      toast({ title: "Could not copy to clipboard", description: "Check your browser's clipboard permissions and try again.", variant: "destructive" });
    }
  }

  const environmentCount = environmentRows.filter(row => row.key || row.value).length;
  const launchUnavailable = isCreating || !organizationId || !canStartInstance || tunnelUnavailable || !!labelError || !!launchError || isUploading;

  return (
    <section aria-label="Device configuration" className="@container/configuration w-full min-w-0">
      <div className="space-y-5">
        <div className="grid min-w-0 items-start gap-x-6 gap-y-2 @min-[32rem]/configuration:grid-cols-[11rem_minmax(0,1fr)]">
          <Label htmlFor="os-select" className="block leading-5 @min-[32rem]/configuration:py-2">Operating system</Label>
          <Select
            value={selectedOS}
            onValueChange={(value: PlaygroundOS) => {
              snapshotOSDefaultPending.current = false;
              setSelectedOS(value);
              // Only Android 15 has a tablet image.
              if (value === 'Android 14') setSelectedAndroidModel('phone');
              // Apps carry over only between iOS versions that keep the model; any other
              // change can make them unfit (an Android SDK level, another device family).
              const models: readonly IOSModel[] | undefined = isIOSVersion(value) ? IOS_VERSIONS[value].models : undefined;
              const keepsModel = models?.includes(selectedIOSModel) ?? false;
              if (models && !keepsModel) setSelectedIOSModel(models[0]);
              if (!(selectedIOS && keepsModel)) setSelectedAssets([]);
            }}
          >
            <SelectTrigger id="os-select">
              <SelectValue placeholder="Select OS" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="Android 14">Android 14</SelectItem>
                <SelectItem value="Android 15">Android 15</SelectItem>
                {Object.keys(IOS_VERSIONS).map((version) => (
                  <SelectItem key={version} value={version}>{version}</SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {!selectedIOS && (
          <div className="grid min-w-0 items-start gap-x-6 gap-y-2 @min-[32rem]/configuration:grid-cols-[11rem_minmax(0,1fr)]">
            <Label htmlFor="android-model-select" className="block leading-5 @min-[32rem]/configuration:py-2">Model</Label>
            <Select
              value={selectedAndroidModel}
              onValueChange={(value: AndroidModel) => {
                setSelectedAndroidModel(value);
              }}
            >
              <SelectTrigger id="android-model-select">
                <SelectValue placeholder="Select model" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="phone">Phone</SelectItem>
                  {selectedOS !== 'Android 14' && (
                    <SelectItem value="tablet">Tablet</SelectItem>
                  )}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        )}

        {selectedIOS && (
          <div className="grid min-w-0 items-start gap-x-6 gap-y-2 @min-[32rem]/configuration:grid-cols-[11rem_minmax(0,1fr)]">
            <Label htmlFor="ios-model-select" className="block leading-5 @min-[32rem]/configuration:py-2">Model</Label>
            <Select
              value={selectedIOSModel}
              onValueChange={(value: IOSModel) => {
                snapshotOSDefaultPending.current = false;
                setSelectedIOSModel(value);
              }}
            >
              <SelectTrigger id="ios-model-select">
                <SelectValue placeholder="Select model" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {selectedIOS.models.map((model) => (
                    <SelectItem key={model} value={model}>{IOS_MODEL_NAMES[model]}</SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        )}

        {selectedIOS && (
          <ConfigurationOption id="snapshot-title" title="Snapshot">
            <PlaygroundSnapshotPicker
              key={organizationId}
              organizationId={organizationId}
              value={user !== "loading" && user.emailAddress ? snapshotKey : deviceState}
              creating={creatingSnapshot}
              onSelect={selectSnapshot}
              onCreate={key => {
                snapshotOSDefaultPending.current = false;
                setSnapshotSelection({ organizationId, key, createNew: true });
              }}
              canRead={canReadSnapshots}
              disabled={isCreating || !organizationId || (!canReadSnapshots && !deviceState && !creatingSnapshot)}
            />
            {creatingSnapshot && (
              <div className="space-y-2">
                <Label htmlFor="snapshot-key">New snapshot key</Label>
                <Input
                  id="snapshot-key"
                  value={deviceState ?? ""}
                  onChange={event => setSnapshotSelection(current => ({ ...current, key: event.target.value }))}
                  placeholder="e.g. checkout-demo"
                  aria-describedby="snapshot-description snapshot-status"
                  aria-invalid={!!snapshotError}
                  disabled={isCreating}
                  autoFocus
                />
              </div>
            )}
            <p id="snapshot-description" className="text-xs leading-4 text-muted-foreground">
              {creatingSnapshot
                ? "Starts fresh and saves under this key when the device stops."
                : deviceState
                  ? snapshot === null || snapshot?.saved === false
                    ? "Starts fresh and saves under this key when the device stops."
                    : "Restores apps and data, then saves changes when the device stops."
                  : "Choose a snapshot or create one to keep apps and data between devices."}
            </p>
            <div id="snapshot-status">
              {snapshotLoading && <p role="status" className="text-xs text-muted-foreground">Checking snapshot...</p>}
              {snapshotError && <p role="alert" className="text-xs text-destructive">{snapshotError}</p>}
              {snapshotIncompatible && !snapshotValidationError && canStartInstance && (
                <AlertDialog key={`${organizationId}:${snapshotKey}:${selectedOS}`}>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="mt-2 h-auto min-h-8 whitespace-normal py-1.5 text-left" disabled={launchUnavailable}>
                      Start fresh and replace snapshot
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Replace snapshot?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Delete the saved apps and data in <span className="break-all font-medium">{snapshotKey}</span> and start a fresh {selectedOS} device.
                        The device will save a new snapshot under this key when it stops. Deletion cannot be undone, even if the device fails to start.
                      </AlertDialogDescription>
                      <p className="text-sm leading-6 text-muted-foreground">Stop other devices using this key first. They can overwrite the snapshot when they save.</p>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction disabled={launchUnavailable} onClick={() => onStart(playgroundConfiguration(), { replaceSnapshot: true })}>Replace and start</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              {snapshotLoadError && deviceState && <Button variant="outline" size="sm" disabled={isCreating} onClick={() => void refreshSnapshot()}>Retry snapshot</Button>}
            </div>
          </ConfigurationOption>
        )}

        <div className="grid min-w-0 items-start gap-x-6 gap-y-2 @min-[32rem]/configuration:grid-cols-[11rem_minmax(0,1fr)]">
          <Label htmlFor="apps-select" className="block leading-5 @min-[32rem]/configuration:py-2">Apps</Label>
          <div className="min-w-0 space-y-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button id="apps-select" variant="outline" className="h-auto min-h-9 w-full justify-between px-3 py-1.5 text-left font-normal leading-5">
                  <span className="flex min-w-0 items-center gap-2">
                    <Package className="h-4 w-4 shrink-0" />
                    <span className="min-w-0 whitespace-normal break-words">
                      {selectedAssets.length === 0 ? "Select apps" : selectedAssets.map(name => {
                        const asset = assets !== "loading" ? assets.find(asset => asset.name === name) : undefined;
                        return asset ? getAssetDisplayName(asset) : name.replace(/^appstore\//, "");
                      }).join(", ")}
                    </span>
                  </span>
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-72 max-h-80 overflow-y-auto"
                onScroll={handleAssetMenuScroll}
              >
                <DropdownMenuItem
                  onSelect={() => setShowUploadDropzone(true)}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Upload
                </DropdownMenuItem>
                {selectedAssets.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Selected apps</DropdownMenuLabel>
                    {selectedAssets.map(name => {
                      const asset = assets !== "loading" ? assets.find(asset => asset.name === name) : undefined;
                      return (
                        <DropdownMenuCheckboxItem
                          key={name}
                          checked
                          onCheckedChange={() => setSelectedAssets(current => current.filter(selected => selected !== name))}
                          onSelect={event => event.preventDefault()}
                        >
                          <span className="min-w-0 break-all">{asset ? getAssetDisplayName(asset) : name.replace(/^appstore\//, "")}</span>
                        </DropdownMenuCheckboxItem>
                      );
                    })}
                  </>
                )}
                <div className="sticky top-0 z-10 bg-popover p-1">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={assetSearch}
                      onChange={(event) => setAssetSearch(event.target.value)}
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                      placeholder="Search apps..."
                      className="h-9 pl-8"
                    />
                  </div>
                </div>
                {(() => {
                  if (assets === "loading") {
                    return (
                      <div className="flex items-center justify-center py-4">
                        <Loader className="h-4 w-4 animate-spin" />
                      </div>
                    );
                  }
                  // Filter assets based on selected OS
                  const osFilter = selectedIOS ? 'ios' : 'android';
                  const normalizedSearch = assetSearch.trim().toLowerCase();
                  const filteredAssets = assets
                    .filter(a => !selectedAssets.includes(a.name))
                    .filter(a => !a.os || a.os === osFilter)
                    .filter(a => assetMatchesSearch(a, normalizedSearch));

                  if (filteredAssets.length === 0) {
                    return (
                      <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                        {assetSearch ? "No matching apps" : selectedAssets.length ? "No other apps available" : "No assets available"}
                      </div>
                    );
                  }

                  const appStoreAssets = filteredAssets.filter(isAppStoreAsset);
                  const userAssets = filteredAssets.filter(a => !isAppStoreAsset(a));
                  const visibleUserAssets = userAssets.slice(0, visibleAssetCount);
                  const hasMoreUserAssets = visibleUserAssets.length < userAssets.length;
                  const renderAssetItem = (asset: Asset) => (
                    <DropdownMenuCheckboxItem
                      key={asset.id}
                      checked={selectedAssets.includes(asset.name)}
                      onCheckedChange={(checked) => {
                        setSelectedAssets(prev =>
                          checked === true
                            ? [...prev, asset.name]
                            : prev.filter(name => name !== asset.name)
                        );
                      }}
                      onSelect={(e) => e.preventDefault()}
                    >
                      <span className="truncate">{getAssetDisplayName(asset)}</span>
                    </DropdownMenuCheckboxItem>
                  );

                  return (
                    <>
                      {/* App Store assets */}
                      {appStoreAssets.length > 0 && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel>App Store</DropdownMenuLabel>
                          {appStoreAssets.map(renderAssetItem)}
                        </>
                      )}
                      {/* User's own assets */}
                      {userAssets.length > 0 && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel>Your Apps</DropdownMenuLabel>
                          {visibleUserAssets.map(renderAssetItem)}
                          {hasMoreUserAssets && (
                            <div className="px-2 py-2 text-center text-xs text-muted-foreground">
                              Scroll to show more apps
                            </div>
                          )}
                        </>
                      )}
                    </>
                  );
                })()}
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Upload Dropzone */}
            {showUploadDropzone && !isUploading && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowUploadDropzone(false)}
                  className="absolute -top-1 -right-1 z-10 p-0.5 rounded-full bg-muted hover:bg-muted-foreground/20"
                >
                  <X className="h-3 w-3" />
                </button>
                <div
                  role="button"
                  tabIndex={0}
                  className={`
                      flex flex-col items-center justify-center gap-2 p-6
                      border-2 border-dashed rounded-lg cursor-pointer
                      transition-colors duration-200
                      ${isDragOver
                      ? 'border-primary bg-primary/5'
                      : 'border-muted-foreground/25 hover:border-muted-foreground/50 hover:bg-muted/50'
                    }
                    `}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragOver(true);
                  }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragOver(false);
                    const file = e.dataTransfer.files[0];
                    if (file) {
                      setSelectedFile(file);
                      setUploadError(null);
                    }
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".apk,.zip,.gz"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      if (file) {
                        setSelectedFile(file);
                        setUploadError(null);
                      }
                    }}
                  />
                  <Upload className="h-6 w-6 text-muted-foreground" />
                  <p className="text-xs text-center text-muted-foreground">
                    Drop file here
                  </p>
                </div>
              </div>
            )}
            {/* Upload Progress */}
            {isUploading && (
              <div className="space-y-2 p-3 border rounded-lg bg-muted/30">
                <div className="flex justify-between text-xs">
                  <span className="font-medium truncate flex-1 mr-2">{selectedFile?.name}</span>
                  <span className="text-muted-foreground whitespace-nowrap">{uploadedSize || 'Starting...'}</span>
                </div>
                <div className="h-1.5 w-full bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-150 ease-out"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelUpload}
                  className="w-full h-7 text-xs text-destructive hover:text-destructive"
                >
                  Cancel
                </Button>
              </div>
            )}
            {/* Upload Error */}
            {uploadError && (
              <div className="text-xs text-destructive">
                {uploadError}
              </div>
            )}
          </div>
        </div>

        <div className="grid min-w-0 items-start gap-x-6 gap-y-2 @min-[32rem]/configuration:grid-cols-[11rem_minmax(0,1fr)]">
          <Label htmlFor="tunnel-select" className="block leading-5 @min-[32rem]/configuration:py-2">Tunnel</Label>
          <div className="min-w-0 space-y-2">
            <Select
              value={selectedTunnel?.id ?? "none"}
              disabled={isCreating || (!canReadTunnels && !selectedTunnel)}
              onValueChange={id => {
                const tunnel = activeTunnels.find(tunnel => tunnel.name === id);
                setTunnelSelection({ organizationId, name: tunnel?.name });
              }}
            >
              <SelectTrigger id="tunnel-select" aria-describedby="tunnel-description">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-w-[calc(100vw-2rem)]">
                <SelectItem value="none">None</SelectItem>
                {selectedTunnel && !activeTunnel && (
                  <SelectItem value={selectedTunnel.id} disabled>
                    <span className="break-all">{selectedTunnel.name} (unavailable)</span>
                  </SelectItem>
                )}
                {activeTunnels.map(tunnel => (
                  <SelectItem key={tunnel.id} value={tunnel.name}>
                    <span className="break-all">{tunnel.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p id="tunnel-description" className="break-words text-xs leading-4 text-muted-foreground">
              To access localhost or internal services,{" "}
              <button
                type="button"
                className="text-primary underline underline-offset-4 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isCreating || !canManageTunnels || !organizationId}
                onClick={() => setCreatingTunnelFor(organizationId)}
              >
                create a private tunnel
              </button>
            </p>
            {(denied("tunnel", "read") || tunnelLoadError || !tunnelsLoaded || !canReadTunnels || tunnelUnavailable) && (
              <p role="status" className="text-xs text-muted-foreground">
                {denied("tunnel", "read")
                  ? "Private tunnels require permission to read this organization's tunnels."
                  : tunnelLoadError
                    ? "Could not load private tunnels."
                    : !tunnelsLoaded || !canReadTunnels
                      ? "Loading private tunnels..."
                      : tunnelUnavailable
                        ? "This tunnel is no longer active. Choose another tunnel or None."
                        : null}
              </p>
            )}
            {tunnelLoadError && canReadTunnels && (
              <Button variant="outline" size="sm" onClick={() => void refreshTunnels()} disabled={isCreating}>
                Retry tunnels
              </Button>
            )}
            {canManageTunnels && organizationId && (
              <NewTunnelDialog
                key={organizationId}
                organizationId={organizationId}
                open={creatingTunnelFor === organizationId}
                onOpenChange={open => setCreatingTunnelFor(open ? organizationId : undefined)}
              />
            )}
          </div>
        </div>

      </div>
      <Collapsible className="group/options mt-6 border-t py-4">
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="h-9 w-full justify-start gap-2 px-0 text-sm font-medium hover:bg-transparent">
            <ChevronDown aria-hidden className="-rotate-90 transition-transform group-data-[state=open]/options:rotate-0" />
            More options
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-5 pb-2 pt-4">
          <ConfigurationOption id="instance-labels-title" title="Labels">
            <div role="group" aria-labelledby="instance-labels-title" className="space-y-2">
              {labelRows.map((row, index) => (
                <div key={row.id} className="grid min-h-9 grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2rem] items-start rounded-md border border-input">
                  <Input
                    aria-label={`Label ${index + 1} key`}
                    placeholder="Key"
                    value={row.key}
                    onChange={event => updateLabel(row.id, "key", event.target.value)}
                    disabled={isCreating}
                    aria-describedby={labelError ? "labels-error" : undefined}
                    className="h-8.5 min-w-0 rounded-none rounded-l-md border-0 focus-visible:ring-inset"
                  />
                  <Input
                    aria-label={`Label ${index + 1} value`}
                    placeholder="Value"
                    value={row.value}
                    onChange={event => updateLabel(row.id, "value", event.target.value)}
                    disabled={isCreating}
                    className="h-8.5 min-w-0 rounded-none border-0 border-l border-input focus-visible:ring-inset"
                  />
                  {(row.key || row.value) && <Button
                    variant="ghost"
                    size="icon"
                    className="mt-0.5 h-7 w-7"
                    aria-label={`Remove label ${index + 1}`}
                    disabled={isCreating}
                    onClick={() => setLabelRows(rows => rows.filter(label => label.id !== row.id))}
                  >
                    <X aria-hidden />
                  </Button>}
                </div>
              ))}
            </div>
          </ConfigurationOption>

          <ConfigurationOption id="launch-env-title" title="Environment variables">
            <div role="group" aria-labelledby="launch-env-title" className="space-y-2">
              {environmentRows.map((row, index) => (
                <div key={row.id} className="grid min-h-9 grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2rem] items-start rounded-md border border-input">
                  <Input
                    aria-label={`Environment variable ${index + 1} name`}
                    placeholder="Name"
                    value={row.key}
                    onChange={event => updateEnvironment(row.id, "key", event.target.value)}
                    disabled={isCreating}
                    className="h-8.5 min-w-0 rounded-none rounded-l-md border-0 focus-visible:ring-inset"
                  />
                  <Textarea
                    aria-label={`Environment variable ${index + 1} value`}
                    placeholder="Value"
                    rows={Math.min(6, row.value.split("\n").length)}
                    value={row.value}
                    onChange={event => updateEnvironment(row.id, "value", event.target.value)}
                    disabled={isCreating}
                    className="min-h-8.5 min-w-0 resize-y rounded-none border-0 border-l border-input py-1.5 leading-5 focus-visible:ring-inset"
                  />
                  {(row.key || row.value) && <Button
                    variant="ghost"
                    size="icon"
                    className="mt-0.5 h-7 w-7"
                    aria-label={`Remove environment variable ${index + 1}`}
                    disabled={isCreating}
                    onClick={() => setEnvironmentRows(rows => rows.filter(entry => entry.id !== row.id))}
                  >
                    <X aria-hidden />
                  </Button>}
                </div>
              ))}
            </div>
          </ConfigurationOption>
          <ConfigurationOption id="launch-url-title" title="Open URL">
            <Input id="launch-url" aria-labelledby="launch-url-title" placeholder="myapp://path or https://example.com" value={openUrl} onChange={event => setOpenUrl(event.target.value)} disabled={isCreating} />
          </ConfigurationOption>
          <ConfigurationOption id="inactivity-timeout-title" title="Inactivity timeout">
            <Input id="inactivity-timeout" aria-labelledby="inactivity-timeout-title" placeholder="Organization default" value={inactivityTimeout} onChange={event => setInactivityTimeout(event.target.value)} disabled={isCreating} />
            <p className="text-xs leading-4 text-muted-foreground">For example, 5m or 1h.</p>
          </ConfigurationOption>
        </CollapsibleContent>
        {labelError && <p id="labels-error" role="alert" className="mt-2 text-xs text-destructive">{labelError}</p>}
        {launchError && <p role="alert" className="mt-2 text-xs text-destructive">{launchError}</p>}
      </Collapsible>
      <div className="sticky bottom-0 z-10 space-y-2 border-t bg-background py-4">
        {viewOnlyRole && (
          <p className="text-xs text-muted-foreground">
            Your role can view instances but not start them.
          </p>
        )}
        <div className="flex sm:justify-end">
          <Button
            className="min-w-0 flex-1 rounded-r-none sm:flex-none sm:px-8"
            onClick={() => onStart(playgroundConfiguration())}
            disabled={launchUnavailable || snapshotUnavailable}
          >
            {isCreating ? (
              <>
                <Loader className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              "Start device"
            )}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                className="shrink-0 rounded-l-none border-l border-primary-foreground/20"
                aria-label="Copy device configuration"
                disabled={isCreating || !organizationId || tunnelUnavailable || !!labelError || !!launchError || snapshotUnavailable || isUploading}
              >
                <ChevronDown aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {(environmentCount > 0 || openUrl || (selectedIOS && deviceState)) && <DropdownMenuLabel className="max-w-64 text-xs font-normal text-muted-foreground">Use Copy as URL to include launch options.</DropdownMenuLabel>}
              <DropdownMenuItem disabled={environmentCount > 0 || !!openUrl || !!(selectedIOS && deviceState)} onSelect={() => void copyConfiguration("command")}>Copy as command</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => void copyConfiguration("url")}>Copy as URL</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </section>
  );
}
